<script lang="ts">
export const MODULE_LEVEL_SELECTION = 'module'
</script>

<script lang="ts" setup>
import {
  ToccoModuleRunFieldsFragment,
  ToccoSeminarRegistrationQueryDocument,
  ToccoSeminarRegistrationQueryQuery,
  ToccoSeminarRegistrationQueryQueryVariables,
} from '~/api/graphql/generated'
import { isELearningTeachingMethod, isRunRegistrationOpen, formatToccoPrice } from '~/lib/utils/toccoUtils'
import { useTypedAsyncQuery } from '~/lib/utils/graphqlDocument'

export interface SeminarRegistrationModule {
  id: string
  fieldExternalId: string
  title: string
  coursePrice: number | string | null
  isELearning: boolean
  hasRuns: boolean
  optional: boolean
  runs: ToccoModuleRunFieldsFragment[]
}

export interface SeminarRegistrationSeminar {
  id: string
  title: string
  fieldExternalId: string
}

export interface SeminarRunSelection {
  module: SeminarRegistrationModule
  run: ToccoModuleRunFieldsFragment | null
}

const props = defineProps<{
  seminarId: string
}>()

const emit = defineEmits<{
  confirm: [payload: { seminar: SeminarRegistrationSeminar; selections: SeminarRunSelection[] }]
}>()

const { t } = useI18n()

type SeminarEntity = Extract<
  NonNullable<NonNullable<ToccoSeminarRegistrationQueryQuery['toccoQuery']>['entities']>[number],
  { __typename?: 'ToccoSeminar' }
>

interface BaseModuleShape {
  __typename?: string
  id?: number | null
  title?: string | null
  status?: boolean | null
  fieldExternalId?: string | null
  fieldToccoAbbreviation?: string | null
  fieldCoursePrice?: number | string | null
  fieldTeachingMethod?: { entity?: { fieldEnvironment?: string | null } | null } | null
  reverseFieldBaseModuleTocco?: { entities?: Array<ToccoModuleRunFieldsFragment | null> | null } | null
}

const variables: ToccoSeminarRegistrationQueryQueryVariables = { id: [props.seminarId] }
const { data } = await useTypedAsyncQuery(ToccoSeminarRegistrationQueryDocument, variables)

const seminarEntity = computed(
  () =>
    (data.value?.toccoQuery?.entities ?? []).find(
      (entity): entity is SeminarEntity => entity?.__typename === 'ToccoSeminar',
    ) ?? null,
)

const mapModules = (
  references: ReadonlyArray<{ entity?: unknown } | null> | null | undefined,
  optional: boolean,
): SeminarRegistrationModule[] =>
  (references ?? [])
    .map((reference) => reference?.entity as BaseModuleShape | null | undefined)
    .filter((module): module is BaseModuleShape =>
      Boolean(module && module.__typename === 'ToccoBaseModule' && module.status),
    )
    .sort((a, b) => (a.fieldToccoAbbreviation ?? '').localeCompare(b.fieldToccoAbbreviation ?? ''))
    .map((module) => {
      const moduleRuns = (module.reverseFieldBaseModuleTocco?.entities ?? []).filter(
        (run): run is ToccoModuleRunFieldsFragment => Boolean(run && run.__typename === 'ToccoModuleRun'),
      )
      const runs = moduleRuns
        .filter((run) => run.status && isRunRegistrationOpen(run))
        .sort((a, b) => (a.fieldStartDate?.value ?? '').localeCompare(b.fieldStartDate?.value ?? ''))
      return {
        id: (module.id ?? '').toString(),
        fieldExternalId: module.fieldExternalId ?? '',
        title: module.title ?? '',
        coursePrice: module.fieldCoursePrice ?? null,
        isELearning: isELearningTeachingMethod(module.fieldTeachingMethod),
        hasRuns: moduleRuns.length > 0,
        optional,
        runs,
      }
    })

const requiredModules = computed(() => mapModules(seminarEntity.value?.fieldRequiredBaseModules, false))
const optionalModules = computed(() => {
  const requiredModuleIds = new Set(requiredModules.value.map((module) => module.id))
  return mapModules(seminarEntity.value?.fieldOptionalBaseModules, true).filter(
    (module) => !requiredModuleIds.has(module.id),
  )
})
const allModules = computed(() => [...requiredModules.value, ...optionalModules.value])

const hasModules = computed(() => allModules.value.length > 0)

const hasModuleLevelSelection = (module: SeminarRegistrationModule) =>
  module.isELearning && !module.hasRuns && Boolean(module.fieldExternalId.trim())

const selectedRunByModule = reactive<Record<string, string>>({})
watchEffect(() => {
  allModules.value.forEach((module) => {
    if (selectedRunByModule[module.id] === undefined) {
      selectedRunByModule[module.id] = module.optional
        ? ''
        : (module.runs[0]?.id ?? (hasModuleLevelSelection(module) ? MODULE_LEVEL_SELECTION : '')).toString()
    }
  })
})

const groups = computed(() =>
  [
    { title: t('tocco.detail.seminarModules.requiredModules'), modules: requiredModules.value },
    { title: t('tocco.detail.seminarModules.optionalModules'), modules: optionalModules.value },
  ].filter((group) => group.modules.length > 0),
)

const selections = computed<SeminarRunSelection[]>(() => {
  const seenModuleIds = new Set<string>()
  return allModules.value.reduce<SeminarRunSelection[]>((result, module) => {
    if (seenModuleIds.has(module.id)) return result
    seenModuleIds.add(module.id)

    if (selectedRunByModule[module.id] === MODULE_LEVEL_SELECTION && hasModuleLevelSelection(module)) {
      result.push({ module, run: null })
      return result
    }
    const run = module.runs.find((candidate) => (candidate.id ?? '').toString() === selectedRunByModule[module.id])
    if (run) result.push({ module, run })
    return result
  }, [])
})

const canConfirm = computed(() => selections.value.length > 0)

const onConfirm = () => {
  if (!canConfirm.value || !seminarEntity.value) return

  emit('confirm', {
    seminar: {
      id: (seminarEntity.value.id ?? '').toString(),
      title: seminarEntity.value.title ?? '',
      fieldExternalId: seminarEntity.value.fieldExternalId ?? '',
    },
    selections: selections.value,
  })
}
</script>

<template>
  <div class="seminar-run-selection">
    <Section class="form-title">
      <div class="tl-10 tl-o-1">
        <h6>{{ t('tocco.registration.seminar.headings.subtitle') }}</h6>
        <h1>{{ seminarEntity?.title }}</h1>
      </div>
    </Section>

    <template v-if="hasModules">
      <SectionSpecial v-for="group in groups" :key="group.title" :title="group.title">
        <div class="modules">
          <div v-for="module in group.modules" :key="module.id" class="module">
            <div class="module-header">
              <h4 :id="`module-title-${module.optional ? 'opt' : 'req'}-${module.id}`" class="module-title h3">
                {{ module.title }}
              </h4>
              <span v-if="formatToccoPrice(module.coursePrice)" class="module-price">
                {{ formatToccoPrice(module.coursePrice) }}
              </span>
            </div>
            <div
              v-if="module.runs.length || hasModuleLevelSelection(module)"
              class="runs"
              role="radiogroup"
              :aria-labelledby="`module-title-${module.optional ? 'opt' : 'req'}-${module.id}`"
            >
              <CourseRunRow
                v-for="run in module.runs"
                :key="run.id ?? ''"
                :run="run"
                :name="`seminar-module-${module.id}`"
                :e-learning="module.isELearning"
                :selected="selectedRunByModule[module.id] === (run.id ?? '').toString()"
                @select="selectedRunByModule[module.id] = (run.id ?? '').toString()"
              />
              <CourseRunRow
                v-if="hasModuleLevelSelection(module)"
                :name="`seminar-module-${module.id}`"
                :e-learning="true"
                :selected="selectedRunByModule[module.id] === MODULE_LEVEL_SELECTION"
                @select="selectedRunByModule[module.id] = MODULE_LEVEL_SELECTION"
              />
              <ToccoRunDeclineRow
                :name="`seminar-module-${module.id}`"
                :label="
                  t(module.optional ? 'tocco.runSelection.declineModule' : 'tocco.runSelection.declineRequiredModule')
                "
                :selected="!selectedRunByModule[module.id]"
                @select="selectedRunByModule[module.id] = ''"
              />
            </div>
            <p v-else class="module-unavailable text-small">{{ t('tocco.runSelection.noDateAvailable') }}</p>
          </div>
        </div>
      </SectionSpecial>

      <Section class="cta-wrapper">
        <div class="tl-10 tl-o-1">
          <p v-if="selections.length === 0" class="selection-hint">
            {{ t('tocco.runSelection.selectAtLeastOne') }}
          </p>
          <Button primary slide class="cta" :disabled="!canConfirm" @click="onConfirm">
            {{ t('tocco.detail.seminarModules.registerButtonText') }}
          </Button>
        </div>
      </Section>
    </template>

    <Section v-else>
      <div class="tl-10 tl-o-1">
        <p class="empty-state">{{ t('tocco.runSelection.emptyState') }}</p>
      </div>
    </Section>
  </div>
</template>

<style lang="scss" scoped>
// Module-group band headings are full module names; match the run-selection
// band's widened title column (artboard reserves ~6 columns, default ds-4).
:deep(.header .text) {
  @include breakpoint(ds) {
    --span: 6;
  }
}

.form-title {
  h6 {
    margin-bottom: 9px;
  }

  h1 {
    margin-bottom: 0;
  }
}

.modules {
  display: flex;
  flex-direction: column;
  gap: var(--gap);
}

.module {
  min-width: 0;
  background: var(--c-white);
  border: 1px solid var(--c-line-light);
}

.module-header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px 16px;

  padding: 24px 16px;
  background: var(--c-background-medium);

  @include breakpoint(ts) {
    padding: 40px 32px;
  }

  .module-title {
    min-width: 0;
    margin-bottom: 0;
    overflow-wrap: anywhere;
  }

  .module-price {
    color: var(--c-primary);
    font-weight: bold;
    white-space: nowrap;

    @include f-24;
  }
}

.runs {
  min-width: 0;
}

.runs,
.module-unavailable {
  padding-inline: 16px;

  @include breakpoint(ts) {
    padding-inline: 24px;
  }
}

.module-unavailable {
  margin-bottom: 0;
  padding-block: 20px;

  color: var(--c-gray-100);
}

.cta-wrapper {
  .selection-hint {
    margin-bottom: 16px;

    color: var(--c-primary);
  }

  .cta {
    margin-top: 8px;
  }
}

.empty-state {
  margin-bottom: 0;
}
</style>
